// ConciergePlaceCard - Expanded place card with full details

import React, { useState } from 'react';
import { MapPin, ChevronDown, ChevronUp, Clock, Wifi, Star, ExternalLink } from 'lucide-react';
import type { Place } from '../../types';
import { WIFI_QUALITY_LABELS } from '../../types';

interface ConciergePlaceCardProps {
  place: Place;
  reason: string;
  onViewDetails: () => void;
}

export function ConciergePlaceCard({ place, reason, onViewDetails }: ConciergePlaceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const imageUrl = place.images?.[0] || place.imageUrl;

  // Format opening hours display
  const getOpeningStatus = () => {
    if (place.openingHours?.displayText) {
      return place.openingHours.displayText;
    }
    return null;
  };

  // Get key amenities for GoodWork places
  const getAmenities = () => {
    const items: string[] = [];
    if (place.amenities?.wifi && place.amenities.wifi !== 'none') {
      items.push(WIFI_QUALITY_LABELS[place.amenities.wifi]);
    }
    if (place.amenities?.powerOutlets === 'many') {
      items.push('Viele Steckdosen');
    }
    if (place.amenities?.meetingRooms) {
      items.push('Meeting-Räume');
    }
    if (place.amenitiesText) {
      // Take first 50 chars of amenities text
      items.push(place.amenitiesText.substring(0, 50) + (place.amenitiesText.length > 50 ? '...' : ''));
    }
    return items;
  };

  const openingStatus = getOpeningStatus();
  const amenities = getAmenities();
  const hasExpandedContent = openingStatus || amenities.length > 0 || place.goodFactor || place.tagline;

  return (
    <div className="w-full bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-indigo-300 hover:shadow-md transition-all">
      {/* Main Card Content */}
      <div className="flex items-start gap-3 p-3">
        {/* Image */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={place.name}
            className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-6 h-6 text-gray-400" />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-semibold text-gray-900">{place.name}</h4>
              <p className="text-xs text-gray-500">{place.district || place.city}</p>
            </div>
            {place.rating > 0 && (
              <div className="flex items-center gap-1 text-xs text-amber-600 flex-shrink-0">
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                <span>{place.rating.toFixed(1)}</span>
              </div>
            )}
          </div>

          {/* Full Reason - NO truncation */}
          <p className="text-sm text-indigo-600 mt-2 leading-relaxed">
            {reason}
          </p>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && hasExpandedContent && (
        <div className="px-3 pb-3 pt-0 space-y-2 border-t border-gray-100 mt-2">
          {/* Tagline */}
          {place.tagline && (
            <p className="text-sm text-gray-600 italic">"{place.tagline}"</p>
          )}

          {/* GoodFactor */}
          {place.goodFactor && (
            <p className="text-sm text-gray-700">{place.goodFactor}</p>
          )}

          {/* Opening Hours */}
          {openingStatus && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4 text-gray-400" />
              <span>{openingStatus}</span>
            </div>
          )}

          {/* Amenities */}
          {amenities.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <Wifi className="w-4 h-4 text-gray-400 mt-0.5" />
              <span>{amenities.join(' · ')}</span>
            </div>
          )}

          {/* Vibe Tags */}
          {(place.vibeTags?.length || place.goodworkVibeTags?.length) && (
            <div className="flex flex-wrap gap-1 mt-2">
              {(place.vibeTags || place.goodworkVibeTags || []).slice(0, 4).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-xs rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex border-t border-gray-100">
        {hasExpandedContent && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-1 flex items-center justify-center gap-1 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                <span>Weniger</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                <span>Mehr Details</span>
              </>
            )}
          </button>
        )}
        <button
          onClick={onViewDetails}
          className="flex-1 flex items-center justify-center gap-1 py-2.5 text-sm text-indigo-600 font-medium hover:bg-indigo-50 transition-colors border-l border-gray-100"
        >
          <ExternalLink className="w-4 h-4" />
          <span>Vollständig ansehen</span>
        </button>
      </div>
    </div>
  );
}

export default ConciergePlaceCard;
